import z from 'zod';
import { businessSchema } from '../../common/app-registry.schema';
import { dateSchema } from '../../validation';

/**
 * Every funnel step any business can use — one shared vocabulary, not one
 * enum per funnel.
 *
 * The step list deliberately does NOT belong to a funnel. Businesses
 * overlap heavily (everybody has "created an account", "added a truck",
 * "paid") and the moment each funnel owns its own names, the same real
 * event is called two things and no report can span brands. So this is
 * the catalog; each business's `FUNNEL_CONFIG` entry picks the subset it
 * uses and declares their order.
 *
 * This replaces the seven-number `PROMO_STEPS` enum. Numbers were the
 * root of the "it feels weird in conversation" problem: a numbered list
 * invites the model to narrate "step 3 of 7", which is both wrong the
 * moment a funnel changes length and meaningless to someone who never
 * agreed to a seven-step process. Nothing can say "step 3" if there is
 * no 3.
 *
 * Adding a step here is cheap and safe; every funnel opts in explicitly.
 * Removing one is not — completed steps are persisted on `Lead.funnels`,
 * so a removed value breaks reads of existing documents.
 */
export const funnelStepSchema = z.enum([
  /**
   * Contact was made, in either direction — they messaged us, we
   * messaged them, a rep called, they called, or they signed themselves
   * up off the website.
   *
   * Direction-neutral on purpose, and that is the whole point of the
   * name. Its two predecessors were `cold_reach` and then
   * `outbound_sent`: both described *our* motion, so a lead that arrived
   * inbound could never complete the first step of its own funnel. The
   * card then showed "We reached out — Next" above a completed "They
   * replied", pointing a rep at work that had already happened in
   * reverse — and every inbound lead was stuck one step short of a
   * complete funnel forever.
   *
   * Always completed, because a lead only exists once somebody made
   * contact. Which kind of contact it was is on
   * `meta.firstTouch.kind` — the immutable attribution snapshot —
   * rather than encoded in the step name. This is per-business progress
   * and fires again when a second business first reaches the same person.
   */
  'first_interaction',
  /**
   * They said something. The real "engaged" signal: a delivered template
   * proves nothing about interest, a reply proves everything.
   *
   * Completed at the same instant as `first_interaction` when the first
   * interaction was theirs — an inbound message IS them talking, and
   * making them wait for a second one would report an engaged prospect
   * as merely contacted.
   */
  'replied',
  /** A `User` (and usually an `Org`) now exists. */
  'account_created',
  /** Government waybill issuance is switched on — Bayan / Logisti connected. */
  'waybill_enabled',
  /** At least one driver and/or vehicle registered. */
  'fleet_added',
  /** First order created. */
  'first_order',
  /** First government waybill actually issued. */
  'first_waybill',
  /**
   * They have seen what it costs.
   *
   * The step that separates "never got the pitch" from "got it and did
   * not buy", which is the single most useful thing to know about a lead
   * that stalls after its first waybill — and before this step the two
   * were the same row. Completed when the price ladder is actually shown
   * to them, whichever tool showed it and whichever kind of account they
   * hold.
   */
  'pricing_shown',
  /**
   * An invoice exists and is payable — the checkout link is out.
   *
   * Not money: it is the last thing WE do before it is entirely up to
   * them, so an invoice with no `paid` beside it is the precise shape of
   * a lost sale and the thing a follow-up should chase. Deliberately
   * separate from `paid`, which used to absorb it and so could not tell
   * "never asked to buy" from "asked, invoiced, never paid".
   */
  'invoice_created',
  /** Money arrived. Not "checkout link created" — see `FUNNEL_CONFIG`. */
  'paid',
  /** Post-purchase survey answered. */
  'survey_done',
  /**
   * STOP / unsubscribe.
   *
   * A step like any other, so status rules can name it (`lost:
   * 'opted_out'`) instead of needing a parallel concept for "negative
   * events". It is a fact about what they did, not a judgement about
   * their fit — which is exactly what separates it from `unqualified`.
   */
  'opted_out',
]);
export type FunnelStep = z.infer<typeof funnelStepSchema>;

/** One completed step, with when and whatever the completing tool knew. */
export const funnelStepRecordSchema = z.object({
  step: funnelStepSchema,
  at: dateSchema,
  /**
   * Free-form facts from the tool that completed the step — the plan
   * code on `paid`, the vehicle id on `fleet_added`. Not queried; read
   * by the admin drawer and by humans reconstructing a conversion.
   *
   * Free-form in shape, not in type: **document ids go in as ObjectId**,
   * never as hex strings. Two of the writers stringified, so the same
   * `billingId` was a string on `invoice_created` and an ObjectId on
   * `fleet_added` — which makes `metaValue` hand the next step a value
   * whose type depends on which tool wrote it, and puts strings in a
   * database where every other id is an ObjectId. Anything reading these
   * over HTTP still sees a hex string; that is JSON, not the document.
   */
  meta: z.record(z.unknown()).optional(),
});
export type FunnelStepRecord = z.infer<typeof funnelStepRecordSchema>;

/**
 * One business's funnel progress for one lead.
 *
 * A lead can meet us more than once — a Wathiqa Plus thread that later
 * wants Hero — so `Lead.funnels` is an array of these rather than a
 * single embedded funnel. `business` on each entry is the brand key
 * (one funnel per business, not per audience product).
 *
 * There is no `id`: a funnel id IS the business name, so storing both
 * would be one fact in two fields that can disagree.
 */
export const leadFunnelSchema = z.object({
  business: businessSchema,
  /**
   * Completed steps, append-only, at most one record per step.
   *
   * Per-step timestamps are the whole point and the reason this is not
   * a `FunnelStep[]`: the CRM question is "when did they issue their
   * first waybill", and a bare array of names cannot answer it.
   */
  completed: z.array(funnelStepRecordSchema).default([]),
  startedAt: dateSchema,
  lastStepAt: dateSchema.optional(),
  /** STOP — nothing outbound may go to this lead for this business again. */
  optedOutAt: dateSchema.optional(),
});
export type LeadFunnel = z.infer<typeof leadFunnelSchema>;
